import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  GenericExperienceFe,
} from "@intelligems/ig-types";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { Grid, useTheme } from "@mui/material";
import { getVariationDtoFromFe } from "@src/api/formatters/from-dto/variations";
import { RolloutAnalyticsTable } from "@src/features/analytics/tables/RolloutsAnalyticsTable";
import { useFileBuild } from "@src/features/experiments/hooks/use-file-build";
import { getDialog, setDialog } from "@src/store/slices/global";
import { multiCurrencyTesting } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import { PriceRolloutWarning } from "./PriceRolloutWarning";

interface MarketPriceUpdaterDialogProps {
  currExperience: GenericExperienceFe;
  variations: (Omit<ExperienceListVariationDto, "experienceId"> & {
    experienceId?: string;
  })[];
}

export const MarketPriceUpdaterDialog = ({
  currExperience,
  variations,
}: MarketPriceUpdaterDialogProps) => {
  const dispatch = useDispatch();
  const marketPriceUpdater = useSelector(getDialog("marketPriceUpdater"));
  const theme = useTheme();
  const { buildPriceCsvFile, downloadTemplateReady } = useFileBuild({
    experience: currExperience,
  });

  const [selectedVariationId, setSelectedVariationId] = useImmer<string>(
    variations.find((variation) => variation.isControl)?.id || variations[0].id
  );

  const handleClose = () => {
    dispatch(
      setDialog({
        dialog: "marketPriceUpdater",
        open: false,
        props: { action: "none" },
      })
    );
  };

  const continueAction = () => {
    if (!marketPriceUpdater.props) {
      return;
    }
    dispatch(
      setDialog({
        dialog: "marketUpdateConfirm",
        open: true,
        props: {
          action: marketPriceUpdater.props.action,
          testGroupId: selectedVariationId,
        },
      })
    );
    handleClose();
  };

  const onDownloadClick = () => {
    const currVariation = variations.find(
      (tg) => tg.id === selectedVariationId
    );
    let tempVariations: (Omit<ExperienceListVariationDto, "experienceId"> & {
      experienceId?: string;
    })[] = [];
    if (currVariation) {
      tempVariations = [currVariation];
    }

    buildPriceCsvFile.mutate({
      body: {
        variations: getVariationDtoFromFe(tempVariations as VariationFe[]),
        selectedProducts: [],
        name: currExperience.name,
        experienceId: currExperience.id,
        forExternalPriceUpload: true,
      },
      pathParams: {
        type: "csv",
      },
      queryParams: undefined,
    });
  };

  return marketPriceUpdater.isOpen ? (
    <IgDialog
      title={`Upload Prices to Your Market ${
        marketPriceUpdater?.props?.action === "pause"
          ? "& Pause Test"
          : "& End Test"
      }`}
      rawContent={
        <Grid container direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography sx={{ color: theme.palette.text.secondary }}>
              Select the test group that you'd like to roll out, then use the
              CSV to upload prices in Shopify Markets or Global-e before
              continuing.
              <IgTypography
                igStyle={"link"}
                sx={{ fontWeight: 400 }}
                onClick={() => {
                  openNewTab(multiCurrencyTesting);
                }}
              >
                {" "}
                Find out more.
              </IgTypography>
            </IgTypography>
          </Grid>
          <RolloutAnalyticsTable
            experience={currExperience}
            variations={variations}
            selectedVariationId={selectedVariationId}
            setSelectedVariationId={setSelectedVariationId}
          />

          {Object.values(currExperience.testTypes).filter(
            (value) => value === true
          ).length > 1 && (
            <Grid item>
              <PriceRolloutWarning experience={currExperience} />
            </Grid>
          )}
          <Grid
            container
            direction={"row"}
            item
            justifyContent={"space-between"}
          >
            <IgButton
              variant={"contained"}
              igVariant={downloadTemplateReady.isReady ? "success" : undefined}
              loading={buildPriceCsvFile.isPending}
              onClick={() => {
                if (
                  downloadTemplateReady.isReady &&
                  downloadTemplateReady.href
                ) {
                  window.open(downloadTemplateReady.href, "_blank");
                } else {
                  onDownloadClick();
                }
              }}
              disabled={!selectedVariationId}
            >
              {downloadTemplateReady.isReady
                ? "CSV Ready - Click to Download"
                : "Download CSV"}
            </IgButton>
            <IgButton onClick={continueAction}>
              I've downloaded the CSV, continue
            </IgButton>
          </Grid>
        </Grid>
      }
      handleClose={handleClose}
      hideActions={true}
      fullWidth={true}
      showCloseIcon={true}
      maxWidth={"lg"}
      showDialog={marketPriceUpdater.isOpen}
      dialogHeight={null}
    />
  ) : null;
};
